import {
  ConnectionQualityLevels,
  ConnectionStates,
  StreamStates,
} from "@minervaproject/avsubsystem-core";
import clsx from "clsx";
import { isNumber } from "lodash-es";
import { ChangeEvent, useMemo, useState } from "react";
import { UAParser } from "ua-parser-js";

import { MonitoringEventTypeType } from "../../shared/types/monitoring";
import { PreclassTestOutcomeType } from "../../shared/types/preclass-check";

import { TD, TDStatusIconWithTooltip } from "./Table";

import { PreclassCheckMessages } from "components/av/preclass-check-messages";
import { Tooltip } from "mds/components/Tooltip";
import { CircleErrorRedIcon } from "mds/icons/CircleErrorRed";
import { CircleSuccessGreenIcon } from "mds/icons/CircleSuccessGreen";
import { MicrophoneIcon } from "mds/icons/Microphone";
import { MicrophoneOffIcon } from "mds/icons/MicrophoneOff";
import { VideoIcon } from "mds/icons/Video";
import { VideoOffIcon } from "mds/icons/VideoOff";
import { useAppSelector } from "store/index";
import {
  SectionUserActivityStatus,
  selectActivePageIdForUser,
  selectActivityStatusForUser,
  selectClassUserIdByUserId,
  selectClassUserIsInVirtualClassByUserId,
  selectCurrentSectionId,
  selectFollowingStatusForUser,
  selectIsClassSessionVirtual,
  selectIsPresentationActive,
  selectMonitoringEventCountBySectionIdAndTargetClassUserIdAndType,
  selectMonitoringEventCountBySectionIdAndUserIdAndType,
  selectPresentingUserId,
  selectSectionRoleByUserId,
  selectUserIdsInCurrentSection,
  selectUserNameById,
} from "store/selectors";
import { selectVirtualUserState } from "store/selectors/pubsub-virtual-class-state";
import { useListSelector, useObjectSelector } from "store/store-hooks";
import { deviceErrorForDisplay } from "utils/av";
import { buildDatadogFacetedUrl } from "utils/monitoring";

function formatMs(value: number | undefined | null): string {
  return isNumber(value) ? `${Math.round(value)} ms` : "-";
}

type UserTypeFilterPropsType = {
  filterValue: string[];
  setFilterValue: (filterValue: string[]) => void;
};

function UserTypeFilter({ filterValue, setFilterValue }: UserTypeFilterPropsType) {
  const onFilterChange = (userType: string) => (event: ChangeEvent) => {
    const { checked } = event.target as HTMLInputElement;
    const newFilter = checked
      ? [...filterValue, userType]
      : filterValue.filter((type) => type !== userType);
    setFilterValue(newFilter);
  };

  return (
    <div className="mb-4">
      <ul className="border-gray-100 border p-3">
        <li className="inline pr-5">User Filter: </li>
        <li className="inline pr-5">
          <input
            checked={filterValue.includes("PRESENT")}
            id="usertype-present"
            type="checkbox"
            onChange={onFilterChange("PRESENT")}
          />
          <label className="ml-2" htmlFor="usertype-present">
            Present Participants
          </label>
        </li>
        <li className="inline pr-5">
          <input
            checked={filterValue.includes("ABSENT")}
            id="usertype-absent"
            type="checkbox"
            onChange={onFilterChange("ABSENT")}
          />
          <label className="ml-2" htmlFor="usertype-absent">
            Absent Participants
          </label>
        </li>
      </ul>
    </div>
  );
}

function UserStatsRow({ filterValue, userId }: { filterValue: string[]; userId: string }) {
  const sectionId = useAppSelector(selectCurrentSectionId);
  const userName = useAppSelector((s) => selectUserNameById(s, userId));
  const role = useAppSelector((s) => selectSectionRoleByUserId(s, userId));

  const isVirtualClass = useAppSelector(selectIsClassSessionVirtual);
  const isPresentationActive = useAppSelector(selectIsPresentationActive);
  const presentingUserId = useAppSelector(selectPresentingUserId);
  const isPresenting = userId === presentingUserId;

  const classUserId = useAppSelector((s) => selectClassUserIdByUserId(s, userId));
  const classUserIsInVirtualClass = useAppSelector((s) =>
    selectClassUserIsInVirtualClassByUserId(s, userId),
  );
  const pageId = useAppSelector((s) => selectActivePageIdForUser(s, userId));
  const classUserIsFollowing = useAppSelector((s) => selectFollowingStatusForUser(s, userId));
  const activityStatus = useAppSelector((s) => selectActivityStatusForUser(s, userId));
  const isUserActive = activityStatus === SectionUserActivityStatus.ACTIVE;

  // TODO: compute class duration
  const duration = 60;

  const pageLoadCount = useAppSelector((state) =>
    selectMonitoringEventCountBySectionIdAndUserIdAndType(
      state,
      sectionId,
      userId,
      MonitoringEventTypeType.ENTER_CLASS,
    ),
  );
  const isPresent = pageLoadCount > 0;

  const normalizedPageLoadCount = (duration && pageLoadCount / (duration / 60)) || 0;
  const websocketDisconnectionCount = useAppSelector((state) =>
    selectMonitoringEventCountBySectionIdAndUserIdAndType(
      state,
      sectionId,
      userId,
      MonitoringEventTypeType.WEBSOCKET_DISCONNECTED,
    ),
  );
  const normalizedWebsocketDisconnectionCount =
    (duration && websocketDisconnectionCount / (duration / 60)) || 0;
  const failedApiRequestCount = useAppSelector((state) =>
    selectMonitoringEventCountBySectionIdAndUserIdAndType(
      state,
      sectionId,
      userId,
      MonitoringEventTypeType.FAILED_API_REQUEST,
    ),
  );
  const normalizedFailedApiRequestCount =
    (duration && failedApiRequestCount / (duration / 60)) || 0;
  const pageWorksheetLoadErrorCount = useAppSelector((state) =>
    selectMonitoringEventCountBySectionIdAndUserIdAndType(
      state,
      sectionId,
      userId,
      MonitoringEventTypeType.PAGE_WORKSHEET_LOAD_ERROR,
    ),
  );
  const normalizedPageWorksheetLoadErrorCount =
    (duration && pageWorksheetLoadErrorCount / (duration / 60)) || 0;
  const poorConnectionWarningCount = useAppSelector((state) =>
    selectMonitoringEventCountBySectionIdAndUserIdAndType(
      state,
      sectionId,
      userId,
      MonitoringEventTypeType.POOR_CONNECTION_WARNING,
    ),
  );
  const normalizedPoorConnectionWarningCount =
    (duration && poorConnectionWarningCount / (duration / 60)) || 0;
  const avRefreshCount = useAppSelector((state) =>
    selectMonitoringEventCountBySectionIdAndUserIdAndType(
      state,
      sectionId,
      userId,
      MonitoringEventTypeType.AV_REFRESH,
    ),
  );
  const normalizedAvRefreshCount = (duration && avRefreshCount / (duration / 60)) || 0;

  const avPresentInAnotherTabCount = useAppSelector((state) =>
    selectMonitoringEventCountBySectionIdAndUserIdAndType(
      state,
      sectionId,
      userId,
      MonitoringEventTypeType.AV_PRESENT_IN_ANOTHER_TAB,
    ),
  );

  const streamReloadCount = useAppSelector((state) =>
    selectMonitoringEventCountBySectionIdAndUserIdAndType(
      state,
      sectionId,
      userId,
      MonitoringEventTypeType.RELOAD_STREAM,
    ),
  );

  const targetStreamReloadCount = useAppSelector((state) =>
    selectMonitoringEventCountBySectionIdAndTargetClassUserIdAndType(
      state,
      sectionId,
      classUserId,
      MonitoringEventTypeType.RELOAD_STREAM,
    ),
  );

  const virtualUserState = useObjectSelector((state) => selectVirtualUserState(state, classUserId));

  const {
    avRtt,
    cameraError,
    connectionQuality,
    connectionState,
    micError,
    currentCamera,
    currentMic,
    currentSpeaker,
    isAudioEnabled,
    isSpeaking,
    isVideoEnabled,
    navigationTimingMetrics,
    preclassCheck,
    pubsubPingRtt,
    pubsubWebsocketStatus,
    publishedStreamState,
    shareDBPingRtt,
    shareDBWebsocketStatus,
    userAgent,
  } = virtualUserState || {};

  const avIssue =
    !!micError ||
    !!cameraError ||
    connectionState === ConnectionStates.failed ||
    publishedStreamState === StreamStates.failed;

  const isConnectedToPubsub = pubsubWebsocketStatus === "connected";
  const isConnectingToPubsub = pubsubWebsocketStatus === "connecting";
  const isConnectedToShareDB = shareDBWebsocketStatus === "connected";
  const isConnectingToShareDB = shareDBWebsocketStatus === "connecting";

  const { browser, os } = useMemo(() => {
    const parser = userAgent && new UAParser(userAgent);
    if (parser) {
      return parser.getResult();
    }
    return { browser: null, os: null };
  }, [userAgent]);

  const preClassCheckOutcome = preclassCheck?.outcome;
  const preClassTests = useMemo(() => preclassCheck?.tests || [], [preclassCheck?.tests]);
  const preClassCheckFailed = preClassTests.some(({ outcome }) =>
    [
      PreclassTestOutcomeType.FAIL,
      PreclassTestOutcomeType.ABORT,
      PreclassTestOutcomeType.EXCEPTION,
    ].includes(outcome),
  );
  const preClassCheckMessage =
    PreclassCheckMessages.getHighestPriorityMessageFromResults(
      preClassTests.reduce((obj, val) => ({ ...obj, [val.name]: val }), {}),
    )?.message || "";

  if (!userId || !sectionId) {
    return null;
  }

  if (isPresent && !filterValue.includes("PRESENT")) {
    return null;
  }
  if (!isPresent && !filterValue.includes("ABSENT")) {
    return null;
  }

  return (
    <tr data-testid={`user-stats-${userId}`}>
      <TD>
        <div className="w-22 flex items-center gap-2">
          {/* <div className="relative h-6 w-6">
            {/* <SignalsAndReactions userId={userId} size="sm" noAnimation /> */}
          {/* </div> */}
          {isVirtualClass && (
            <>
              <div
                className={clsx("my-1 h-4 w-4 rounded-full", {
                  "bg-blue": isSpeaking,
                })}
              />
              <div className="relative flex h-4 w-4 items-center justify-center">
                {isAudioEnabled ? <MicrophoneIcon /> : <MicrophoneOffIcon className="text-red" />}
              </div>
              <div className="relative flex h-4 w-4 items-center justify-center">
                {isVideoEnabled ? <VideoIcon /> : <VideoOffIcon className="text-red" />}
              </div>
            </>
          )}
          <a href={buildDatadogFacetedUrl({ sectionId, userId })} rel="noreferrer" target="_blank">
            🐕
          </a>
        </div>
      </TD>
      <TD dataTestId={`user-stats-${userId}-id`}>{userId}</TD>
      <TD dataTestId={`user-stats-${userId}-name`}>
        <span className={clsx({ "font-bold": isUserActive })}>{userName}</span>
      </TD>
      <TD dataTestId={`user-stats-${userId}-role`}>{role || "-"}</TD>
      {isVirtualClass && (
        <TD dataTestId={`user-stats-${userId}-is-in-av`}>
          {classUserIsInVirtualClass ? <CircleSuccessGreenIcon /> : <CircleErrorRedIcon />}
        </TD>
      )}
      {isPresentationActive ? (
        <TD className="center">
          <Tooltip text={<span className="text-left text-black">Page ID: {pageId || "-"}</span>}>
            {classUserIsFollowing || isPresenting ? (
              <CircleSuccessGreenIcon />
            ) : (
              <CircleErrorRedIcon />
            )}
          </Tooltip>
        </TD>
      ) : (
        <TD dataTestId={`user-stats-${userId}-page-id`}>{pageId || "-"} </TD>
      )}

      <TD
        conditionForDanger={normalizedPageLoadCount >= 2}
        conditionForWarning={pageLoadCount > 1}
        dataTestId={`user-stats-${userId}-page-loads`}
      >
        {`${pageLoadCount}`}
      </TD>
      <TD
        conditionForDanger={normalizedWebsocketDisconnectionCount >= 2}
        conditionForWarning={websocketDisconnectionCount > 1}
        dataTestId={`user-stats-${userId}-websocket-disconnections`}
      >
        {`${websocketDisconnectionCount}`}
      </TD>
      <TD
        conditionForDanger={normalizedFailedApiRequestCount >= 1}
        dataTestId={`user-stats-${userId}-failed-api-requests`}
      >
        {`${failedApiRequestCount}`}
      </TD>
      <TD
        conditionForDanger={normalizedPageWorksheetLoadErrorCount >= 1}
        dataTestId={`user-stats-${userId}-page-worksheet-load-errors`}
      >
        {`${pageWorksheetLoadErrorCount}`}
      </TD>
      <TD
        conditionForDanger={normalizedPoorConnectionWarningCount >= 1}
        dataTestId={`user-stats-${userId}-poor-connection-warnings`}
      >
        {`${poorConnectionWarningCount}`}
      </TD>
      {isVirtualClass && (
        <>
          <TD
            conditionForWarning={avPresentInAnotherTabCount > 1}
            dataTestId={`user-stats-${userId}-av-present-in-another-tab`}
          >
            {`${avPresentInAnotherTabCount}`}
          </TD>
          <TD
            conditionForDanger={normalizedAvRefreshCount >= 2}
            conditionForWarning={avRefreshCount > 1}
            dataTestId={`user-stats-${userId}-av-refreshes`}
          >
            {`${avRefreshCount}`}
          </TD>
          <TD
            conditionForDanger={streamReloadCount >= 3 || targetStreamReloadCount >= 3}
            conditionForWarning={streamReloadCount > 1 || targetStreamReloadCount > 1}
            dataTestId={`user-stats-${userId}-stream-reload`}
            noWrap
          >
            {`${streamReloadCount} / ${targetStreamReloadCount}`}
          </TD>
        </>
      )}

      {/* Page Load Timing */}
      <TDStatusIconWithTooltip
        conditionForDanger={navigationTimingMetrics?.network_quality === "poor"}
        conditionForWarning={navigationTimingMetrics?.network_quality === "fair"}
        dataTestId={`user-stats-${userId}-page-load-timing`}
      >
        {virtualUserState && (
          <div className="text-left text-black">
            <div className="font-bold">Page Load Timing</div>
            <div className="pl-2 pt-1">
              <div>Performance Score: {navigationTimingMetrics?.performance_score}</div>
              <div
                className={clsx({
                  "text-red": navigationTimingMetrics?.network_quality === "poor",
                  "text-yellow": navigationTimingMetrics?.network_quality === "fair",
                  "text-green":
                    navigationTimingMetrics?.network_quality === "good" ||
                    navigationTimingMetrics?.network_quality === "excellent",
                })}
              >
                Network Quality: {navigationTimingMetrics?.network_quality}
              </div>
              <div>DNS Lookup: {formatMs(navigationTimingMetrics?.dns_lookup_ms)}</div>
              <div>TCP Connection: {formatMs(navigationTimingMetrics?.tcp_connection_ms)}</div>
              <div>SSL Handshake: {formatMs(navigationTimingMetrics?.ssl_handshake_ms)}</div>
              <div>Request Response: {formatMs(navigationTimingMetrics?.request_response_ms)}</div>
              <div>Total Network: {formatMs(navigationTimingMetrics?.total_network_ms)}</div>
              <div>DOM Interactive: {formatMs(navigationTimingMetrics?.dom_interactive_ms)}</div>
              <div>
                DOM Content Loaded: {formatMs(navigationTimingMetrics?.dom_content_loaded_ms)}
              </div>
              <div>DOM Complete: {formatMs(navigationTimingMetrics?.dom_complete_ms)}</div>
              <div>Total Load: {formatMs(navigationTimingMetrics?.total_load_ms)}</div>
              <div>Transfer Size: {navigationTimingMetrics?.transfer_size_bytes}</div>
              <div>Encoded Size: {navigationTimingMetrics?.encoded_size_bytes}</div>
              <div>Decoded Size: {navigationTimingMetrics?.decoded_size_bytes}</div>
              <div>Performs like: {navigationTimingMetrics?.connection?.effectiveType}</div>
            </div>
          </div>
        )}
      </TDStatusIconWithTooltip>
      {/* Pubsub */}
      <TDStatusIconWithTooltip
        conditionForDanger={
          pubsubPingRtt?.isHigh || (!isConnectedToPubsub && !isConnectingToPubsub)
        }
        conditionForWarning={
          !!pubsubPingRtt?.median && !pubsubPingRtt?.isLow && !pubsubPingRtt?.isHigh
        }
        dataTestId={`user-stats-${userId}-ping-rtt`}
      >
        {virtualUserState && (
          <div className="text-left text-black">
            <div className="font-bold">Pubsub Status</div>
            <div className="pl-2 pt-1">
              <div
                className={clsx({
                  "text-red": !isConnectedToPubsub,
                  "text-green": isConnectedToPubsub,
                })}
              >
                {pubsubWebsocketStatus ?? "-"}
              </div>
            </div>
            <div className="mt-2 font-bold">Ping RTT</div>
            <div className="pl-2 pt-1">
              <div>Min: {pubsubPingRtt?.min ?? "-"}</div>
              <div>Median: {pubsubPingRtt?.median ?? "-"}</div>
              <div>Max: {pubsubPingRtt?.max ?? "-"}</div>
              <div>
                Is High:{" "}
                {pubsubPingRtt?.isHigh ? (
                  <span className="text-red">Yes</span>
                ) : (
                  <span className="text-green">No</span>
                )}
              </div>
              <div>
                Is Low:{" "}
                {pubsubPingRtt?.isLow ? (
                  <span className="text-green">Yes</span>
                ) : (
                  <span className="text-red">No</span>
                )}
              </div>
            </div>
          </div>
        )}
      </TDStatusIconWithTooltip>

      {/* ShareDB */}
      <TDStatusIconWithTooltip
        conditionForDanger={
          shareDBPingRtt?.isHigh || (!isConnectedToShareDB && !isConnectingToShareDB)
        }
        conditionForWarning={
          !!shareDBPingRtt?.median && !shareDBPingRtt?.isLow && !shareDBPingRtt?.isHigh
        }
        dataTestId={`user-stats-${userId}-sharedb-ping-rtt`}
      >
        {virtualUserState && (
          <div className="text-left text-black">
            <div className="font-bold">ShareDB Status</div>
            <div className="pl-2 pt-1">
              <div
                className={clsx({
                  "text-red": !isConnectedToShareDB,
                  "text-green": isConnectedToShareDB,
                })}
              >
                {shareDBWebsocketStatus ?? "-"}
              </div>
            </div>
            <div className="mt-2 font-bold">Ping RTT</div>
            <div className="pl-2 pt-1">
              <div>Min: {shareDBPingRtt?.min ?? "-"}</div>
              <div>Median: {shareDBPingRtt?.median ?? "-"}</div>
              <div>Max: {shareDBPingRtt?.max ?? "-"}</div>
              <div>
                Is High:{" "}
                {shareDBPingRtt?.isHigh ? (
                  <span className="text-red">Yes</span>
                ) : (
                  <span className="text-green">No</span>
                )}
              </div>
              <div>
                Is Low:{" "}
                {shareDBPingRtt?.isLow ? (
                  <span className="text-green">Yes</span>
                ) : (
                  <span className="text-red">No</span>
                )}
              </div>
            </div>
          </div>
        )}
      </TDStatusIconWithTooltip>

      {/* A/V */}
      {isVirtualClass && (
        <>
          <TDStatusIconWithTooltip
            conditionForDanger={avIssue}
            conditionForWarning={connectionQuality === ConnectionQualityLevels.POOR}
            dataTestId={`user-stats-${userId}-av`}
          >
            {virtualUserState && connectionState && (
              <div className="text-left text-black">
                <div className="font-bold">A/V State</div>
                <div className="pl-2 pt-1">
                  {cameraError && (
                    <div>
                      Camera Error:{" "}
                      <span className="text-red">{deviceErrorForDisplay(cameraError)}</span>
                    </div>
                  )}
                  {micError && (
                    <div>
                      Mic Error: <span className="text-red">{deviceErrorForDisplay(micError)}</span>
                    </div>
                  )}
                  {connectionState === ConnectionStates.connected && connectionQuality && (
                    <div>
                      Connection quality:{" "}
                      <span
                        className={clsx({
                          "text-red": connectionQuality === ConnectionQualityLevels.POOR,
                          "text-green": connectionQuality === ConnectionQualityLevels.GOOD,
                        })}
                      >
                        {connectionQuality}
                      </span>
                    </div>
                  )}
                  <div>
                    Connection state:{" "}
                    <span
                      className={clsx({
                        "text-red": connectionState === ConnectionStates.failed,
                        "text-green": connectionState === ConnectionStates.connected,
                      })}
                    >
                      {connectionState}
                    </span>
                  </div>
                  <div>
                    Published stream state:{" "}
                    <span
                      className={clsx({
                        "text-red": publishedStreamState === StreamStates.failed,
                        "text-green": publishedStreamState === StreamStates.playable,
                      })}
                    >
                      {publishedStreamState || "No Published Stream"}
                    </span>
                  </div>
                  {/* TODO: what is a good value for this? What's the unit? should we display a warning if it's too high? */}
                  <div>
                    RTT: <span className="text-green">{avRtt}</span>
                  </div>
                </div>
                <div className="mt-2 font-bold">Devices</div>
                <div className="pl-2 pt-1">
                  <div>
                    Camera: <span className="text-green">{currentCamera}</span>
                  </div>
                  <div>
                    Mic: <span className="text-green">{currentMic}</span>
                  </div>
                  <div>
                    Speaker: <span className="text-green">{currentSpeaker}</span>
                  </div>
                </div>
              </div>
            )}
          </TDStatusIconWithTooltip>
          <TDStatusIconWithTooltip
            conditionForDanger={preClassCheckFailed}
            conditionForWarning={preClassCheckOutcome === PreclassTestOutcomeType.FAIL}
            dataTestId={`user-stats-${userId}-preclass-checks`}
          >
            {preClassCheckOutcome && (
              <div className="text-left text-black">
                <div className="font-bold">Pre-class Checks</div>
                <div className="pl-2 pt-1">
                  {preClassTests.map((test) => (
                    <div key={Math.random().toString(36).substring(7)}>
                      {test.category} / {test.name}:{" "}
                      <span
                        className={clsx({
                          "text-green": test.outcome === PreclassTestOutcomeType.SUCCESS,
                          "text-red": ![
                            PreclassTestOutcomeType.SUCCESS,
                            PreclassTestOutcomeType.NA,
                            PreclassTestOutcomeType.IN_PROGRESS,
                          ].includes(test.outcome),
                        })}
                      >
                        {test.outcome}
                      </span>
                    </div>
                  ))}
                  {preClassCheckMessage && (
                    <div className="mt2">
                      <div>Highest priority error message:</div>
                      <div className="text-red">{preClassCheckMessage}</div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </TDStatusIconWithTooltip>
        </>
      )}
      <TD dataTestId={`user-stats-${userId}-browser`}>
        {browser ? `${browser.name} ${browser.version}` : "-"}
      </TD>
      <TD dataTestId={`user-stats-${userId}-os`}>{os ? `${os.name} ${os.version}` : "-"}</TD>
    </tr>
  );
}

export function UserStats() {
  const sectionId = useAppSelector(selectCurrentSectionId);
  const userIds = useListSelector(selectUserIdsInCurrentSection);

  const isVirtualClass = useAppSelector(selectIsClassSessionVirtual);
  const isPresentationActive = useAppSelector(selectIsPresentationActive);
  const [filterValue, setFilterValue] = useState(["PRESENT"]);

  if (!sectionId) {
    return null;
  }

  return (
    <div>
      <UserTypeFilter filterValue={filterValue} setFilterValue={setFilterValue} />
      <table className="mds-table table-hover" data-testid="user-stats-table">
        <thead>
          <tr>
            <th>{` `}</th>
            <th>User ID</th>
            <th>User Name</th>
            <th>Role</th>
            {isVirtualClass && <th>Is In A/V</th>}
            {isPresentationActive ? <th>Is Following</th> : <th>Page ID</th>}
            <th>Page Loads</th>
            <th>Websocket Disconnections</th>
            <th>Failed API Requests</th>
            <th>Page Load Errors</th>
            <th>Poor Connection Warnings</th>
            {isVirtualClass && (
              <>
                <th>
                  <Tooltip text="Number of times the user has been present in a secondary AV tab">
                    Moved to other tab
                  </Tooltip>
                </th>
                <th>
                  <Tooltip text="Number of AV refreshes">AV Refreshes</Tooltip>
                </th>
                <th>
                  <Tooltip text="Number of stream reloads (user initiated / user targeted)">
                    Stream Reloads
                  </Tooltip>
                </th>
              </>
            )}
            <th>Page Load Timing</th>
            <th>Pubsub</th>
            <th>ShareDB</th>
            {isVirtualClass && (
              <>
                <th>A/V</th>
                <th>
                  <Tooltip text="Results of latest pre-class checks">Pre-class Checks</Tooltip>
                </th>
              </>
            )}
            <th>Browser</th>
            <th>OS</th>
          </tr>
        </thead>
        <tbody>
          {userIds.map((userId) => (
            <UserStatsRow filterValue={filterValue} key={userId} userId={userId} />
          ))}
        </tbody>
      </table>
    </div>
  );
}
